/**
 * Proposal Builder Page - Simplified Multi-Section UI
 * 
 * User uploads ToR once, then extracts individual sections
 * Sections displayed in accordions, can export all to Word
 */

import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    Container,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Divider,
    IconButton,
    Collapse,
    Stack,
    Chip
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckCircleIcon,
    Download as DownloadIcon,
    ContentCopy as ContentCopyIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    AutoFixHigh as AutoFixHighIcon
} from '@mui/icons-material';

interface SectionData {
    extracted: boolean;
    extracting: boolean;
    data: any | null;  // Flexible to handle both v1.0 and v2.0 response formats
    error: string | null;
    progress?: {
        currentPass: number;
        totalPasses: number;
        passName: string;
    };
}

// v2.0 Response Format (for reference)
interface V2Response {
    success: boolean;
    text?: string;  // Professional prose
    extraction_data?: any;  // Raw JSON
    rag_sources?: Array<{
        title: string;
        organization: string;
        similarity: string;
    }>;
    validation_report?: {
        hallucinations_found: number;
        hallucinations: any[];
    };
    metadata?: {
        pipeline: string;
        pass_1_temperature?: number;
        pass_2_temperature?: number;
        pass_3_temperature?: number;
        pass_4_temperature?: number;
        rag_examples_used?: number;
    };
}

const ProposalBuilderPage: React.FC = () => {
    // ========================================================================
    // Helper: Extract text from v2.0 response format
    // ========================================================================
    const getSectionText = (data: any, sectionName: string): string => {
        if (!data) return '';
        
        // v2.0 format: check for text field or specific section_text field
        if (data.text) return data.text;
        if (data[`${sectionName}_text`]) return data[`${sectionName}_text`];
        
        // Legacy format fallback
        if (data.extraction_data) return JSON.stringify(data.extraction_data, null, 2);
        
        return JSON.stringify(data, null, 2);
    };

    // ToR Upload State
    const [torFile, setTorFile] = useState<File | null>(null);
    const [torId, setTorId] = useState<string>('');  // For Team/Budget/Risk (tor_uploads table)
    const [sessionId, setSessionId] = useState<string>('');  // For Workplan (tor_sessions table)
    const [uploading, setUploading] = useState(false);
    const [uploadCollapsed, setUploadCollapsed] = useState(false);

    // Section States
    const [sections, setSections] = useState<{
        methodology: SectionData;
        methodologySchema: SectionData;
        teamStructure: SectionData;
        workplan: SectionData;
        budget: SectionData;
        riskManagement: SectionData;
    }>({
        methodology: { extracted: false, extracting: false, data: null, error: null },
        methodologySchema: { extracted: false, extracting: false, data: null, error: null },
        teamStructure: { extracted: false, extracting: false, data: null, error: null },
        workplan: { extracted: false, extracting: false, data: null, error: null },
        budget: { extracted: false, extracting: false, data: null, error: null },
        riskManagement: { extracted: false, extracting: false, data: null, error: null }
    });

    // UI State
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // ========================================================================
    // ToR Upload Handler
    // ========================================================================
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setTorFile(file);
            setError(null);
        }
    };

    const handleUploadToR = async () => {
        if (!torFile) return;

        setUploading(true);
        setError(null);

        try {
            // Upload ToR as multipart form data (supports PDF, DOCX, TXT)
            const formData = new FormData();
            formData.append('file', torFile);

            const response = await fetch('/api/upload-tor', {
                method: 'POST',
                body: formData
                // Note: Don't set Content-Type header - browser will set it with boundary
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Upload failed');
            }

            const uploadData = await response.json();
            setTorId(uploadData.tor_id);  // Store tor_id for Team/Budget/Risk
            
            // Step 2: Create proposal session for workplan generation
            const sessionResponse = await fetch('/api/create-proposal-session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tor_text: uploadData.tor_text || '',
                    tor_filename: torFile.name
                })
            });

            if (!sessionResponse.ok) {
                throw new Error('Failed to create proposal session');
            }

            const sessionData = await sessionResponse.json();
            setSessionId(sessionData.session_id);  // Store session_id for Workplan
            setSuccess(`✓ ${torFile.name} uploaded and session created successfully`);
            setUploadCollapsed(true);
        } catch (err: any) {
            setError(`Upload failed: ${err.message}`);
        } finally {
            setUploading(false);
        }
    };

    // ========================================================================
    // Section Extraction Handlers
    // ========================================================================
    const extractMethodology = async () => {
        if (!torId) return;

        setSections(prev => ({
            ...prev,
            methodology: { ...prev.methodology, extracting: true, error: null }
        }));

        try {
            const response = await fetch('/api/extract-tor', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tor_id: torId })
            });

            if (!response.ok) {
                throw new Error('Extraction failed');
            }

            const data = await response.json();

            setSections(prev => ({
                ...prev,
                methodology: {
                    extracted: true,
                    extracting: false,
                    data: data.methodology,
                    error: null
                }
            }));
        } catch (err: any) {
            setSections(prev => ({
                ...prev,
                methodology: {
                    ...prev.methodology,
                    extracting: false,
                    error: err.message
                }
            }));
        }
    };

    const extractMethodologySchema = async () => {
        if (!torId) return;

        setSections(prev => ({
            ...prev,
            methodologySchema: { ...prev.methodologySchema, extracting: true, error: null }
        }));

        try {
            const response = await fetch('/api/extract-methodology-schema', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tor_id: torId })
            });

            if (!response.ok) {
                throw new Error('Extraction failed');
            }

            const data = await response.json();

            setSections(prev => ({
                ...prev,
                methodologySchema: {
                    extracted: true,
                    extracting: false,
                    data: data,
                    error: null
                }
            }));
        } catch (err: any) {
            setSections(prev => ({
                ...prev,
                methodologySchema: {
                    ...prev.methodologySchema,
                    extracting: false,
                    error: err.message
                }
            }));
        }
    };

    const extractTeamStructure = async () => {
        if (!torId) return;

        setSections(prev => ({
            ...prev,
            teamStructure: { ...prev.teamStructure, extracting: true, error: null }
        }));

        try {
            // Route through SWA proxy - authentication is validated by the platform
            const response = await fetch('/api/extract-team-structure', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tor_id: torId })
            });

            if (!response.ok) {
                throw new Error('Extraction failed');
            }

            const data = await response.json();

            setSections(prev => ({
                ...prev,
                teamStructure: {
                    extracted: true,
                    extracting: false,
                    data: data, // Contains proposal_text and key_requirements
                    error: null
                }
            }));
        } catch (err: any) {
            setSections(prev => ({
                ...prev,
                teamStructure: {
                    ...prev.teamStructure,
                    extracting: false,
                    error: err.message
                }
            }));
        }
    };

    const extractWorkplan = async () => {
        if (!sessionId) {
            setError('Please upload a ToR first');
            return;
        }

        setSections(prev => ({
            ...prev,
            workplan: { ...prev.workplan, extracting: true, error: null }
        }));

        try {
            const response = await fetch('/api/generate-workplan', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sessionId })
            });

            if (!response.ok) {
                throw new Error(`Failed to generate workplan: ${response.statusText}`);
            }

            const data = await response.json();

            setSections(prev => ({
                ...prev,
                workplan: {
                    extracted: true,
                    extracting: false,
                    data: data,
                    error: null
                }
            }));

            setSuccess('✓ Workplan generated successfully with RAG');
        } catch (err: any) {
            setSections(prev => ({
                ...prev,
                workplan: {
                    ...prev.workplan,
                    extracting: false,
                    error: err.message
                }
            }));
            setError(err.message);
        }
    };

    const extractBudget = async () => {
        if (!torId) return;

        setSections(prev => ({
            ...prev,
            budget: { ...prev.budget, extracting: true, error: null }
        }));

        try {
            const response = await fetch('/api/extract-budget', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tor_id: torId })
            });

            if (!response.ok) {
                throw new Error('Extraction failed');
            }

            const data = await response.json();

            setSections(prev => ({
                ...prev,
                budget: {
                    extracted: true,
                    extracting: false,
                    data: data,
                    error: null
                }
            }));
        } catch (err: any) {
            setSections(prev => ({
                ...prev,
                budget: {
                    ...prev.budget,
                    extracting: false,
                    error: err.message
                }
            }));
        }
    };

    const extractRiskManagement = async () => {
        if (!torId) return;

        setSections(prev => ({
            ...prev,
            riskManagement: { ...prev.riskManagement, extracting: true, error: null }
        }));

        try {
            const response = await fetch('/api/extract-risk-management', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tor_id: torId })
            });

            if (!response.ok) {
                throw new Error('Extraction failed');
            }

            const data = await response.json();

            setSections(prev => ({
                ...prev,
                riskManagement: {
                    extracted: true,
                    extracting: false,
                    data: data,
                    error: null
                }
            }));
        } catch (err: any) {
            setSections(prev => ({
                ...prev,
                riskManagement: {
                    ...prev.riskManagement,
                    extracting: false,
                    error: err.message
                }
            }));
        }
    };

    // ========================================================================
    // Export Handlers
    // ========================================================================
    const handleCopyAll = () => {
        let allText = '';

        if (sections.methodology.extracted) {
            allText += '=== METHODOLOGY ===\n\n';
            allText += JSON.stringify(sections.methodology.data, null, 2) + '\n\n';
        }

        if (sections.teamStructure.extracted) {
            allText += '=== TEAM STRUCTURE ===\n\n';
            allText += JSON.stringify(sections.teamStructure.data, null, 2) + '\n\n';
        }

        // Add other sections as they're implemented

        navigator.clipboard.writeText(allText);
        setSuccess('✓ All content copied to clipboard');
    };

    const handleDownloadWord = () => {
        let docContent = '';

        // Build Word document content
        if (sections.methodologySchema.extracted && sections.methodologySchema.data) {
            docContent += '1. METHODOLOGY\n\n';
            docContent += JSON.stringify(sections.methodologySchema.data.methodology_schema, null, 2);
            docContent += '\n\n\n';
        }

        if (sections.teamStructure.extracted && sections.teamStructure.data) {
            docContent += '2. TEAM COMPOSITION\n\n';
            docContent += sections.teamStructure.data.team_text || JSON.stringify(sections.teamStructure.data.extraction_data, null, 2);
            docContent += '\n\n\n';
        }

        if (sections.workplan.extracted && sections.workplan.data) {
            docContent += '3. WORKPLAN & TIMELINE\n\n';
            docContent += sections.workplan.data.workplan_text || JSON.stringify(sections.workplan.data.workplan, null, 2);
            docContent += '\n\n\n';
        }

        if (sections.budget.extracted && sections.budget.data) {
            docContent += '4. BUDGET AND RESOURCES\n\n';
            docContent += sections.budget.data.budget_text || JSON.stringify(sections.budget.data.extraction_data, null, 2);
            docContent += '\n\n\n';
        }

        if (sections.riskManagement.extracted && sections.riskManagement.data) {
            docContent += '5. RISK MANAGEMENT\n\n';
            docContent += sections.riskManagement.data.risk_text || JSON.stringify(sections.riskManagement.data.extraction_data, null, 2);
            docContent += '\n\n\n';
        }

        if (!docContent) {
            setError('No sections extracted yet. Please extract at least one section first.');
            return;
        }

        // Create and download as text file (simple approach)
        const blob = new Blob([docContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `proposal-${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        setSuccess('✓ Proposal exported as text file');
    };

    // ========================================================================
    // Render
    // ========================================================================
    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom>
                Proposal Builder
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Upload your ToR, extract sections individually, and export to Word
            </Typography>

            {/* Alerts */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            {/* ToR Upload Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        {uploadCollapsed ? '✓ ToR Uploaded' : 'Upload ToR'}
                    </Typography>
                    {uploadCollapsed && (
                        <IconButton size="small" onClick={() => setUploadCollapsed(false)}>
                            <ExpandMoreIcon />
                        </IconButton>
                    )}
                </Stack>

                <Collapse in={!uploadCollapsed}>
                    <Box sx={{ mt: 2 }}>
                        <input
                            accept=".pdf,.doc,.docx,.txt"
                            style={{ display: 'none' }}
                            id="tor-file-upload"
                            type="file"
                            onChange={handleFileSelect}
                        />
                        <label htmlFor="tor-file-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<CloudUploadIcon />}
                                fullWidth
                            >
                                Choose ToR File
                            </Button>
                        </label>

                        {torFile && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Selected: {torFile.name} ({(torFile.size / 1024 / 1024).toFixed(2)} MB)
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={handleUploadToR}
                                    disabled={uploading}
                                    sx={{ mt: 1 }}
                                    fullWidth
                                >
                                    {uploading ? <CircularProgress size={24} /> : 'Upload ToR'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Collapse>

                {uploadCollapsed && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {torFile?.name}
                    </Typography>
                )}
            </Paper>

            {/* Section Extraction */}
            {torId && (
                <>
                    <Typography variant="h6" gutterBottom>
                        Generate Proposal Sections
                    </Typography>

                    {/* 1. Methodology (RAG-Enhanced) */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                <Typography sx={{ flexGrow: 1 }}>
                                    1. Methodology
                                </Typography>
                                {sections.methodologySchema.extracted && <CheckCircleIcon color="success" />}
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Button
                                variant="contained"
                                onClick={extractMethodologySchema}
                                startIcon={<AutoFixHighIcon />}
                                disabled={!torId || sections.methodologySchema.extracting}
                            >
                                {sections.methodologySchema.extracting ? 'Building Schema...' : 'Build Methodology Schema'}
                            </Button>

                            {sections.methodologySchema.extracting && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">
                                        Analysing ToR and retrieving similar methodologies via RAG...
                                    </Typography>
                                </Box>
                            )}

                            {sections.methodologySchema.error && (
                                <Alert severity="error">{sections.methodologySchema.error}</Alert>
                            )}

                            {sections.methodologySchema.extracted && sections.methodologySchema.data && (
                                <Box>
                                    {/* RAG Sources Display */}
                                    {sections.methodologySchema.data.rag_sources && sections.methodologySchema.data.rag_sources.length > 0 && (
                                        <Paper sx={{ p: 2, mb: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                                📚 Similar Proposals Used (RAG):
                                            </Typography>
                                            {sections.methodologySchema.data.rag_sources.map((source: any, idx: number) => (
                                                <Typography key={idx} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
                                                    • {source.title} ({source.organization}) - {(parseFloat(source.similarity) * 100).toFixed(0)}% similar
                                                </Typography>
                                            ))}
                                        </Paper>
                                    )}

                                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                        <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => {
                                            navigator.clipboard.writeText(JSON.stringify(sections.methodologySchema.data.methodology_schema, null, 2));
                                            setSuccess('✓ Methodology schema copied to clipboard');
                                        }}>
                                            Copy Schema
                                        </Button>
                                        <Button size="small" startIcon={<RefreshIcon />} onClick={extractMethodologySchema}>
                                            Regenerate
                                        </Button>
                                    </Stack>
                                    <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
                                        <Typography component="pre" sx={{ 
                                            whiteSpace: 'pre-wrap',
                                            fontFamily: 'monospace',
                                            fontSize: '0.875rem',
                                            overflow: 'auto',
                                            maxHeight: '600px'
                                        }}>
                                            {JSON.stringify(sections.methodologySchema.data.methodology_schema, null, 2)}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {/* 2. Team Structure */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                <Typography sx={{ flexGrow: 1 }}>
                                    2. Team Structure
                                </Typography>
                                {sections.teamStructure.extracted && (
                                    <Chip label="✓ Extracted" size="small" color="success" />
                                )}
                                {sections.teamStructure.extracting && (
                                    <CircularProgress size={20} />
                                )}
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            {!sections.teamStructure.extracted && !sections.teamStructure.extracting && (
                                <Button
                                    variant="contained"
                                    onClick={extractTeamStructure}
                                    startIcon={<AutoFixHighIcon />}
                                >
                                    Extract Team Structure
                                </Button>
                            )}

                            {sections.teamStructure.extracting && (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <CircularProgress />
                                    <Typography variant="body2" sx={{ mt: 2 }}>
                                        Extracting team structure...
                                    </Typography>
                                </Box>
                            )}

                            {sections.teamStructure.error && (
                                <Alert severity="error">{sections.teamStructure.error}</Alert>
                            )}

                            {sections.teamStructure.extracted && sections.teamStructure.data && (
                                <Box>
                                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                        <Chip 
                                            label="Team Requirements Extracted" 
                                            size="small" 
                                            color="success"
                                        />
                                        {sections.teamStructure.data.metadata && (
                                            <Chip 
                                                label={`v${sections.teamStructure.data.metadata.pipeline === 'two-pass' ? '3.0' : '2.0'}`}
                                                size="small" 
                                                color="info"
                                                variant="outlined"
                                            />
                                        )}
                                        <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => {
                                            navigator.clipboard.writeText(sections.teamStructure.data.team_text || JSON.stringify(sections.teamStructure.data.extraction_data, null, 2));
                                            setSuccess('✓ Team section copied to clipboard');
                                        }}>
                                            Copy Text
                                        </Button>
                                        <Button size="small" startIcon={<RefreshIcon />} onClick={extractTeamStructure}>
                                            Regenerate
                                        </Button>
                                    </Stack>
                                    
                                    {/* Enhanced Display with Better Formatting */}
                                    <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
                                        <Typography 
                                            component="div" 
                                            sx={{ 
                                                whiteSpace: 'pre-wrap',
                                                fontFamily: 'Georgia, serif',
                                                lineHeight: 1.8,
                                                '& h2': { 
                                                    fontSize: '1.75rem', 
                                                    fontWeight: 700, 
                                                    mt: 3, 
                                                    mb: 2,
                                                    color: 'primary.main',
                                                    borderBottom: '2px solid',
                                                    borderColor: 'primary.main',
                                                    pb: 1
                                                },
                                                '& h3': { 
                                                    fontSize: '1.35rem', 
                                                    fontWeight: 600, 
                                                    mt: 3, 
                                                    mb: 2,
                                                    color: 'text.primary',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    '&::before': {
                                                        content: '"▸"',
                                                        marginRight: '8px',
                                                        color: 'primary.main'
                                                    }
                                                },
                                                '& p': { mb: 2 },
                                                '& ul': { 
                                                    pl: 3, 
                                                    mb: 2,
                                                    '& li': {
                                                        mb: 1,
                                                        lineHeight: 1.6
                                                    }
                                                },
                                                '& strong': { 
                                                    fontWeight: 600,
                                                    color: 'text.primary'
                                                },
                                                // Style for requirement → response format
                                                '& li:has(strong)': {
                                                    bgcolor: 'action.hover',
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    mb: 1.5,
                                                    borderLeft: '3px solid',
                                                    borderColor: 'primary.main'
                                                }
                                            }}
                                        >
                                            {sections.teamStructure.data.team_text || JSON.stringify(sections.teamStructure.data.extraction_data, null, 2)}
                                        </Typography>
                                    </Paper>
                                    
                                    {/* Show extraction data in collapsible section */}
                                    {sections.teamStructure.data.extraction_data && (
                                        <Accordion sx={{ mt: 2 }}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography variant="body2" color="text.secondary">
                                                    📊 View Extracted Data (JSON)
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 400, overflow: 'auto' }}>
                                                    <pre style={{ margin: 0, fontSize: '0.85rem' }}>
                                                        {JSON.stringify(sections.teamStructure.data.extraction_data, null, 2)}
                                                    </pre>
                                                </Paper>
                                            </AccordionDetails>
                                        </Accordion>
                                    )}
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {/* 3. Workplan & Timeline (RAG-Enhanced) */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                <Typography sx={{ flexGrow: 1 }}>
                                    3. Workplan & Timeline
                                </Typography>
                                {sections.workplan.extracted && <CheckCircleIcon color="success" />}
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Button
                                variant="contained"
                                onClick={extractWorkplan}
                                startIcon={<AutoFixHighIcon />}
                                disabled={!torId || sections.workplan.extracting}
                            >
                                {sections.workplan.extracting ? 'Generating with RAG...' : 'Generate Workplan'}
                            </Button>

                            {sections.workplan.extracting && (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <CircularProgress />
                                    <Typography variant="body2" sx={{ mt: 2 }}>
                                        Analysing ToR and retrieving similar workplans via RAG...
                                    </Typography>
                                </Box>
                            )}

                            {sections.workplan.error && (
                                <Alert severity="error" sx={{ mt: 2 }}>{sections.workplan.error}</Alert>
                            )}

                            {sections.workplan.extracted && sections.workplan.data && (
                                <Box sx={{ mt: 2 }}>
                                    {/* RAG Sources Display */}
                                    {sections.workplan.data.rag_sources && sections.workplan.data.rag_sources.length > 0 && (
                                        <Paper sx={{ p: 2, mb: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                                📚 Similar Workplans Used (RAG):
                                            </Typography>
                                            {sections.workplan.data.rag_sources.map((source: any, idx: number) => (
                                                <Typography key={idx} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
                                                    • {source.filename} ({source.client}) - {source.similarity} similar
                                                </Typography>
                                            ))}
                                        </Paper>
                                    )}

                                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                        <Chip 
                                            label={`${sections.workplan.data.workplan_json?.phases?.length || sections.workplan.data.workplan?.phases?.length || 0} phases`} 
                                            size="small" 
                                            color="info"
                                        />
                                        <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => {
                                            navigator.clipboard.writeText(sections.workplan.data.workplan_text || JSON.stringify(sections.workplan.data.workplan, null, 2));
                                            setSuccess('✓ Workplan copied to clipboard');
                                        }}>
                                            Copy Workplan
                                        </Button>
                                        <Button size="small" startIcon={<RefreshIcon />} onClick={extractWorkplan}>
                                            Regenerate
                                        </Button>
                                    </Stack>
                                    <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
                                        <Typography component="pre" sx={{ 
                                            whiteSpace: 'pre-wrap',
                                            fontFamily: 'monospace',
                                            fontSize: '0.875rem',
                                            overflow: 'auto',
                                            maxHeight: '600px'
                                        }}>
                                            {sections.workplan.data.workplan_text || JSON.stringify(sections.workplan.data.workplan, null, 2)}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {/* 4. Budget & Resources */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                <Typography sx={{ flexGrow: 1 }}>
                                    4. Budget & Resources
                                </Typography>
                                {sections.budget.extracted && <CheckCircleIcon color="success" />}
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Button
                                variant="contained"
                                onClick={extractBudget}
                                startIcon={<AutoFixHighIcon />}
                                disabled={!torId || sections.budget.extracting}
                            >
                                {sections.budget.extracting ? 'Extracting...' : 'Extract Budget'}
                            </Button>

                            {sections.budget.extracting && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">
                                        Analysing budget and resource requirements...
                                    </Typography>
                                </Box>
                            )}

                            {sections.budget.error && (
                                <Alert severity="error">{sections.budget.error}</Alert>
                            )}

                            {sections.budget.extracted && sections.budget.data && (
                                <Box>
                                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                        <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => {
                                            navigator.clipboard.writeText(sections.budget.data.budget_text || JSON.stringify(sections.budget.data.extraction_data, null, 2));
                                            setSuccess('✓ Budget section copied to clipboard');
                                        }}>
                                            Copy Text
                                        </Button>
                                        <Button size="small" startIcon={<RefreshIcon />} onClick={extractBudget}>
                                            Regenerate
                                        </Button>
                                    </Stack>
                                    <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
                                        <Typography 
                                            component="div" 
                                            sx={{ 
                                                whiteSpace: 'pre-wrap',
                                                fontFamily: 'Georgia, serif',
                                                lineHeight: 1.8,
                                                '& h2': { fontSize: '1.5rem', fontWeight: 600, mt: 2, mb: 1 },
                                                '& h3': { fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1 },
                                                '& p': { mb: 1 }
                                            }}
                                        >
                                            {sections.budget.data.budget_text || JSON.stringify(sections.budget.data.extraction_data, null, 2)}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {/* 5. Risk Management */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                <Typography sx={{ flexGrow: 1 }}>
                                    5. Risk Management
                                </Typography>
                                {sections.riskManagement.extracted && <CheckCircleIcon color="success" />}
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Button
                                variant="contained"
                                onClick={extractRiskManagement}
                                startIcon={<AutoFixHighIcon />}
                                disabled={!torId || sections.riskManagement.extracting}
                            >
                                {sections.riskManagement.extracting ? 'Extracting...' : 'Extract Risk Management'}
                            </Button>

                            {sections.riskManagement.extracting && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">
                                        Analysing risks and mitigation strategies...
                                    </Typography>
                                </Box>
                            )}

                            {sections.riskManagement.error && (
                                <Alert severity="error">{sections.riskManagement.error}</Alert>
                            )}

                            {sections.riskManagement.extracted && sections.riskManagement.data && (
                                <Box>
                                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                        <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => {
                                            navigator.clipboard.writeText(sections.riskManagement.data.risk_text || JSON.stringify(sections.riskManagement.data.extraction_data, null, 2));
                                            setSuccess('✓ Risk section copied to clipboard');
                                        }}>
                                            Copy Text
                                        </Button>
                                        <Button size="small" startIcon={<RefreshIcon />} onClick={extractRiskManagement}>
                                            Regenerate
                                        </Button>
                                    </Stack>
                                    <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
                                        <Typography 
                                            component="div" 
                                            sx={{ 
                                                whiteSpace: 'pre-wrap',
                                                fontFamily: 'Georgia, serif',
                                                lineHeight: 1.8,
                                                '& h2': { fontSize: '1.5rem', fontWeight: 600, mt: 2, mb: 1 },
                                                '& h3': { fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1 },
                                                '& p': { mb: 1 }
                                            }}
                                        >
                                            {sections.riskManagement.data.risk_text || JSON.stringify(sections.riskManagement.data.extraction_data, null, 2)}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {/* Export Section */}
                    <Paper sx={{ p: 3, mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Export Proposal
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadWord}
                                disabled={!sections.methodology.extracted && !sections.teamStructure.extracted}
                            >
                                Download Word
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ContentCopyIcon />}
                                onClick={handleCopyAll}
                                disabled={!sections.methodology.extracted && !sections.teamStructure.extracted}
                            >
                                Copy All Text
                            </Button>
                        </Stack>
                    </Paper>
                </>
            )}
        </Container>
    );
};

export default ProposalBuilderPage;
